import { isCatalogFilter, TCatalogFilter } from './catalog-filters';

/** The Menú page's URL state (plan §5.5). */
export interface ICatalogQuery {
  /** The menu shown; null = the store's base menu. */
  mId: number | null;
  /** Jump to this category (removed from the URL once done). */
  cId: number | null;
  /** The product open in the editor. */
  item: number | 'new' | null;
  /** The category open in the editor. */
  category: number | 'new' | null;
  filter: TCatalogFilter;
  q: string;
}

/** Vue Router 3 query values. */
export type TRouteQuery = Record<
  string,
  string | ReadonlyArray<string | null> | null | undefined
>;

const MAX_QUERY_LENGTH = 100;

function first(value: TRouteQuery[string]): string | null {
  if (Array.isArray(value)) return first(value[0]);
  return typeof value === 'string' ? value : null;
}

function positiveInt(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function idOrNew(value: string | null): number | 'new' | null {
  return value === 'new' ? 'new' : positiveInt(value);
}

export interface ICatalogQueryContext {
  /** The store's live menus other than the base one. */
  menuIds: readonly number[];
}

/** Reads the URL, dropping invalid values (including a menu the store does not have). */
export function parseCatalogQuery(
  query: TRouteQuery,
  ctx: ICatalogQueryContext
): ICatalogQuery {
  const mId = positiveInt(first(query.mId));
  const filter = first(query.filter);
  return {
    mId: mId !== null && ctx.menuIds.includes(mId) ? mId : null,
    cId: positiveInt(first(query.cId)),
    item: idOrNew(first(query.item)),
    category: idOrNew(first(query.category)),
    filter: isCatalogFilter(filter) ? filter : 'all',
    q: (first(query.q) ?? '').trim().slice(0, MAX_QUERY_LENGTH),
  };
}

/** The URL query of a state; default values are left out. */
export function serializeCatalogQuery(
  state: ICatalogQuery
): Record<string, string> {
  const query: Record<string, string> = {};
  if (state.mId !== null) query.mId = String(state.mId);
  if (state.cId !== null) query.cId = String(state.cId);
  if (state.item !== null) query.item = String(state.item);
  if (state.category !== null) query.category = String(state.category);
  if (state.filter !== 'all') query.filter = state.filter;
  if (state.q) query.q = state.q;
  return query;
}
